import PropTypes from 'prop-types';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import AppScrollArea from '../../../../components/common/AppScrollArea';
import EventModalMediaContent from '../../../../components/EventModalMediaContent';
import SubtleBadge from '../../../../components/common/SubtleBadge';
import Loader from '../../../../components/Loader';
import OrderApprovalHistory from '../../../order/OrderApprovalHistory';
import { useGetCustomerApprovalHistoryQuery } from '../../../../slices/approvalsApiSlice';
import { getCityNameByCode } from '../../../../utils/cityUtils';
import { getActualCreatorHighlight } from '../../../../utils/approval/approvalListHub';

const STATUS_META = {
	waitingApproval: { bg: 'warning', text: '待审批' },
	inProcessOfApproval: { bg: 'warning', text: '审批中' },
	reject: { bg: 'danger', text: '已驳回' },
	approved: { bg: 'success', text: '已通过' },
	withdrawn: { bg: 'secondary', text: '已撤回' },
};

/** 履历过多时侧栏内滚，避免撑破弹窗 */
const HISTORY_ASIDE_SCROLL_MAX = 'min(24rem, calc(90vh - 14rem))';

const formatDateTime = (date) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');

/** 待办里查看客户申请：Falcon Event Modal；申请侧可再提/取消，审批侧可同意/不同意 */
const CustomerApplyInfoModal = ({
	show,
	onHide,
	approval,
	onRenew,
	onReedit,
	onCancel,
	onWithdraw,
	onApprove,
	onReject,
	isApproving,
	isRejecting,
}) => {
	const { isDark } = useSelector((state) => state.config);
	const detail = approval?.detail || {};
	const statusMeta = STATUS_META[approval?.status] || { bg: 'secondary', text: approval?.status || '客户申请' };
	const isRejected = approval?.status === 'reject';
	const isWithdrawn = approval?.status === 'withdrawn';
	const isWaiting = approval?.status === 'waitingApproval';
	const canDecide = isWaiting && (onApprove || onReject);
	const cityLabel = detail.newCustomerCity ? getCityNameByCode(detail.newCustomerCity) : '';
	const phone = detail.newCustomerPhone || '';
	const email = detail.newCustomerEmail || '';
	const title = approval?.title || detail.newCustomerConcatName || detail.newCustomerName || '客户申请';
	const applicantName = approval?.applicant?.name || '';
	const assistantName = approval?.assistant?.name || '';
	const creatorHighlight = getActualCreatorHighlight({
		entryKind: 'customer',
		applicant: approval?.applicant,
		assistant: approval?.assistant,
	});
	const approvalId = approval?._id;
	const {
		data: fetchedHistory,
		isLoading: isHistoryLoading,
		isFetching: isHistoryFetching,
		isError: isHistoryError,
		isSuccess: isHistorySuccess,
	} = useGetCustomerApprovalHistoryQuery(approvalId, {
		skip: !show || !approvalId,
	});
	const historyReady = !show || !approvalId || isHistorySuccess || isHistoryError || (!isHistoryLoading && !isHistoryFetching);
	const history = Array.isArray(fetchedHistory) ? fetchedHistory : [];
	const latestReject = [...history].reverse().find((item) => item?.status === 'reject');
	const rejectReason =
		String(approval?.approvalSuggestions || latestReject?.approvalSuggestions || '').trim() || '未填写驳回说明';
	const rejectorName = approval?.approver?.name || latestReject?.approver?.name || '-';
	const rejectedAt = approval?.updatedAt || latestReject?.updatedAt;
	const actionBusy = Boolean(isApproving || isRejecting);

	return (
		<Modal show={show} onHide={onHide} contentClassName='border' centered size='lg'>
			<Modal.Header closeButton closeVariant={isDark ? 'white' : undefined} className='bg-body-tertiary px-x1 border-bottom-0'>
				{historyReady ? (
					<Modal.Title className='min-w-0'>
						<h5 className='mb-0 d-flex align-items-center gap-2 flex-nowrap min-w-0'>
							<span className='text-truncate' title={title}>
								{title}
							</span>
							<SubtleBadge bg={statusMeta.bg} pill className='flex-shrink-0'>
								{statusMeta.text}
							</SubtleBadge>
						</h5>
					</Modal.Title>
				) : (
					<div className='flex-grow-1' aria-hidden='true' />
				)}
			</Modal.Header>
			{historyReady ? (
				<>
					<Modal.Body className='p-0 fs-10'>
						<div className='row g-0 align-items-stretch'>
							<div className='col-12 col-md min-w-0 px-x1 pb-x1 pt-1'>
								{isRejected ? (
									<EventModalMediaContent icon='ban' heading='审批驳回信息'>
										<div className='fs-10'>
											<p className='mb-2'>
												<strong>审批人：</strong>
												{rejectorName}
											</p>
											<p className='mb-2'>
												<strong>驳回时间：</strong>
												{formatDateTime(rejectedAt)}
											</p>
											<p className='mb-0'>
												<strong>驳回原因：</strong>
												<span className='text-danger text-break'>{rejectReason}</span>
											</p>
										</div>
									</EventModalMediaContent>
								) : null}
								<EventModalMediaContent icon='building' heading='客户信息'>
									<div className='fs-10'>
										<p className='mb-2'>
											<strong>客户名称：</strong>
											{detail.newCustomerConcatName || detail.newCustomerName || '-'}
										</p>
										<p className='mb-2'>
											<strong>客户类型：</strong>
											{detail.newCustomerType || '-'}
										</p>
										<p className='mb-2'>
											<strong>所属机构：</strong>
											{detail.newCustomerOrganization || '-'}
										</p>
										<p className='mb-2'>
											<strong>所属部门：</strong>
											{detail.newCustomerDepartment || '-'}
										</p>
										<p className='mb-0'>
											<strong>所属分组：</strong>
											{detail.newCustomerGroup || '-'}
										</p>
									</div>
								</EventModalMediaContent>

								<EventModalMediaContent icon='map-marker-alt' heading='联系与地址'>
									<div className='fs-10'>
										<p className='mb-2'>
											<strong>所在城市：</strong>
											{cityLabel || '-'}
										</p>
										<p className='mb-2'>
											<strong>详细地址：</strong>
											{detail.newCustomerAddress || '-'}
										</p>
										<p className='mb-2'>
											<strong>联系电话：</strong>
											{phone ? <a href={`tel:${phone}`}>{phone}</a> : '-'}
										</p>
										<p className='mb-0'>
											<strong>邮箱地址：</strong>
											{email ? <a href={`mailto:${email}`}>{email}</a> : '-'}
										</p>
									</div>
								</EventModalMediaContent>

								<EventModalMediaContent icon='user' heading='申请人'>
									<div className='fs-10'>
										<p className='mb-2'>
											<strong>申请人：</strong>
											<span className={creatorHighlight.applicant ? 'text-primary' : undefined}>{applicantName || '-'}</span>
										</p>
										<p className='mb-2'>
											<strong>销售助理：</strong>
											<span className={creatorHighlight.assistant ? 'text-primary' : undefined}>{assistantName || '-'}</span>
										</p>
										<p className='mb-0'>
											<strong>申请时间：</strong>
											{formatDateTime(approval?.createdAt)}
										</p>
									</div>
								</EventModalMediaContent>
							</div>
							<aside className='col-12 col-md-4 col-lg-3 border-top border-md-top-0 border-md-start border-200 p-3'>
								<AppScrollArea className='app-scroll-area--embedded' maxHeight={HISTORY_ASIDE_SCROLL_MAX}>
									{history.length > 0 ? (
										<OrderApprovalHistory history={history} titleRow className='mb-0' />
									) : (
										<p className='mb-0 fs-10 text-500'>暂无审批历史</p>
									)}
								</AppScrollArea>
							</aside>
						</div>
					</Modal.Body>
					<Modal.Footer className='bg-body-tertiary px-x1 border-top-0'>
						<Button variant='falcon-default' size='sm' className='me-auto' onClick={onHide} disabled={actionBusy}>
							关闭
						</Button>
						{isWaiting && onWithdraw ? (
							<Button variant='primary' size='sm' onClick={onWithdraw} disabled={actionBusy}>
								<FontAwesomeIcon icon='undo' className='fs-11 me-2' />
								撤回
							</Button>
						) : null}
						{isRejected && onCancel ? (
							<Button variant='falcon-default' size='sm' onClick={onCancel} disabled={actionBusy}>
								<FontAwesomeIcon icon='ban' className='fs-11 me-2' />
								取消申请
							</Button>
						) : null}
						{isWithdrawn && onReedit ? (
							<Button variant='falcon-primary' size='sm' onClick={onReedit} disabled={actionBusy}>
								<FontAwesomeIcon icon='edit' className='fs-11 me-2' />
								再次编辑
							</Button>
						) : null}
						{isRejected && onRenew ? (
							<Button variant='falcon-primary' size='sm' onClick={onRenew} disabled={actionBusy}>
								<FontAwesomeIcon icon='edit' className='fs-11 me-2' />
								编辑重提
							</Button>
						) : null}
						{canDecide ? (
							actionBusy ? (
								<Spinner variant='primary' size='sm' />
							) : (
								<>
									{onApprove ? (
										<Button variant='success' size='sm' onClick={onApprove}>
											{isApproving ? <Spinner size='sm' className='me-1' /> : <FontAwesomeIcon icon='check' className='me-1' />}
											同意
										</Button>
									) : null}
									{onReject ? (
										<Button variant='danger' size='sm' onClick={onReject}>
											{isRejecting ? <Spinner size='sm' className='me-1' /> : <FontAwesomeIcon icon='times' className='me-1' />}
											不同意
										</Button>
									) : null}
								</>
							)
						) : null}
					</Modal.Footer>
				</>
			) : (
				<Modal.Body className='px-x1 modal-dots-loading'>
					<Loader />
				</Modal.Body>
			)}
		</Modal>
	);
};

CustomerApplyInfoModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func.isRequired,
	approval: PropTypes.object,
	onRenew: PropTypes.func,
	onReedit: PropTypes.func,
	onCancel: PropTypes.func,
	onWithdraw: PropTypes.func,
	onApprove: PropTypes.func,
	onReject: PropTypes.func,
	isApproving: PropTypes.bool,
	isRejecting: PropTypes.bool,
};

export default CustomerApplyInfoModal;
